"use client";

import {useEffect, useState} from "react";
import Link from "next/link";
import {useRouter} from "next/navigation";
import {ArrowLeft, Calendar, ExternalLink, Loader2, Code2, Sparkles, Globe, User, Building} from "lucide-react";
import {formatDistanceToNow} from "date-fns";

interface ShowcaseApp {
  _id: string;
  name: string;
  description: string;
  prompt: string;
  completedAt: string;
  createdAt: string;
  slug?: string;
  createdBy?: any;
  tenant?: {
    _id: string;
    name: string;
  };
}

export default function ShowcasePage() {
  const [apps, setApps] = useState<ShowcaseApp[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const router = useRouter();

  useEffect(() => {
    fetchApps();
  }, []);

  const fetchApps = async () => {
    try {
      const response = await fetch("/api/apps");
      if (!response.ok) {
        throw new Error("Failed to fetch apps");
      }
      const data = await response.json();
      setApps(data);
    } catch (err) {
      console.error("Error fetching showcase apps:", err);
      setError(err instanceof Error ? err.message : "Failed to load showcase");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return formatDistanceToNow(date, {addSuffix: true});
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 mx-auto mb-4 animate-spin" />
          <p className="text-gray-700 text-lg">Loading showcase...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 text-lg mb-4">Error: {error}</p>
          <button onClick={() => router.push("/")} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-12">
            <div className="flex items-center gap-4">
              <Link href="/" className="flex items-center gap-2 text-gray-700 hover:text-gray-900 transition-colors">
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm">Back</span>
              </Link>
              <div className="w-px h-5 bg-gray-300" />
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-600" />
                <h1 className="text-lg font-semibold text-gray-900">Showcase</h1>
              </div>
            </div>
            <div className="text-xs text-gray-500">
              {apps.length} {apps.length === 1 ? "App" : "Apps"} Built
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {apps.length === 0 ? (
          <div className="text-center py-20">
            <Code2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 text-lg mb-4">No apps in the showcase yet</p>
            <Link href="/" className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors inline-block">
              Create Your First App
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {apps.map(app => (
              <div key={app._id} className="bg-white rounded-lg border border-gray-200 hover:border-gray-300 hover:shadow-md transition-all duration-200 p-4 group">
                <div className="flex items-start justify-between gap-4">
                  {/* Left: Content */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-700 line-clamp-2 mb-2">{app.description}</p>
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      {app.createdBy && (
                        <>
                          <div className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            <span className="text-gray-700 font-medium">{app.createdBy.name}</span>
                          </div>
                          <span className="text-gray-300">•</span>
                        </>
                      )}
                      {app.tenant && (
                        <>
                          <div className="flex items-center gap-1">
                            <Building className="w-3 h-3" />
                            <span className="text-gray-700 font-medium">{app.tenant.name}</span>
                          </div>
                          <span className="text-gray-300">•</span>
                        </>
                      )}
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        <span>{formatDate(app.completedAt || app.createdAt)}</span>
                      </div>
                      {app.slug && (
                        <>
                          <span className="text-gray-300">•</span>
                          <a
                            href={`https://${app.slug}.rtzen.dev`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={e => e.stopPropagation()}
                            className="flex items-center gap-1 text-blue-600 hover:text-blue-700 transition-colors">
                            <Globe className="w-3 h-3" />
                            <span>{app.slug}.rtzen.dev</span>
                          </a>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Right: Actions */}
                  <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        router.push(`/preview/${app._id}`);
                      }}
                      className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors text-sm font-medium flex items-center gap-1.5">
                      <Code2 className="w-3.5 h-3.5" />
                      <span>Edit</span>
                    </button>
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        window.open(`/preview/${app._id}?layout=live`, "_blank");
                      }}
                      className="px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm font-medium flex items-center gap-1.5">
                      <ExternalLink className="w-3.5 h-3.5" />
                      <span>Live</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
